import { useLocalization } from '@lib/localization';
import { MenuItem, TextField } from '@mui/material';
import { ownerOptionStyles } from '@pages/ReportDesigns.styles';
import { ownerLabel, reportDesignLimits, type ReportDesignOwner } from '@store/api/reportDesignEndpoints';
import { BuildingIcon, UserFriendsIcon } from 'react-line-awesome';

interface TextProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

/**
 * A design's name: what every design dialog asks for first, and so where typing starts. Not
 * where it is one setting among others on a page: the browser scrolls to a focused field.
 */
export const NameField: React.FC<TextProps & { autoFocus?: boolean }> = ({ value, onChange, disabled, autoFocus = true }) => {
  const { t } = useLocalization();

  return (
    <TextField
      required
      autoFocus={autoFocus}
      fullWidth
      size="small"
      label={t('Common_Name')}
      helperText={t('ReportDesign_NameHelp')}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      slotProps={{ htmlInput: { maxLength: reportDesignLimits.nameLength } }}
      disabled={disabled}
    />
  );
};

export const DescriptionField: React.FC<TextProps> = ({ value, onChange, disabled }) => {
  const { t } = useLocalization();

  return (
    <TextField
      fullWidth
      multiline
      minRows={2}
      size="small"
      label={t('Common_Description')}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      slotProps={{ htmlInput: { maxLength: reportDesignLimits.descriptionLength } }}
      disabled={disabled}
    />
  );
};

// A select holds strings, and the organization's `teamId` is `null`.
const ORGANIZATION = 'organization';

const valueOf = (owner: ReportDesignOwner): string => owner.teamId ?? ORGANIZATION;

interface OwnerProps {
  /** Where the reader may create a design. */
  owners: ReportDesignOwner[];
  value: ReportDesignOwner | null;
  onChange: (owner: ReportDesignOwner) => void;
  disabled: boolean;
}

/** Who a new design belongs to. Asked once: a design changes owner only by being copied. */
export const OwnerField: React.FC<OwnerProps> = ({ owners, value, onChange, disabled }) => {
  const { t } = useLocalization();

  return (
    <TextField
      select
      fullWidth
      size="small"
      label={t('ReportDesign_Owner')}
      helperText={t('ReportDesign_OwnerHelp')}
      value={value ? valueOf(value) : ''}
      onChange={(event) => {
        const owner = owners.find((candidate) => valueOf(candidate) === event.target.value);

        if (owner) onChange(owner);
      }}
      disabled={disabled}
    >
      {owners.map((owner) => (
        <MenuItem key={valueOf(owner)} value={valueOf(owner)} sx={ownerOptionStyles}>
          {owner.teamId === null ? <BuildingIcon /> : <UserFriendsIcon />} {ownerLabel(owner)}
        </MenuItem>
      ))}
    </TextField>
  );
};
